import React, { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "./AuthContext";
import { DEFAULT_CURRENCY_SYMBOL } from "../utils/currency";

export const AccountContext = createContext();

export const AccountProvider = ({ children }) => {
  const { user } = useAuth();
  // Key by user id so different logins never share the same stored selection
  const storageKey = user?.id ? `selectedAccount_${user.id}` : null;

  const [selectedAccount, setSelectedAccount] = useState("");
  // List of account usernames that have demo_mode=true (populated by UserBox on load)
  const [demoAccounts, setDemoAccounts] = useState([]);
  // Per-account currency symbol (system_settings.currency_symbol). Defaults to "$"
  // for "All Accounts" / accounts with no row yet, so existing displays are unaffected.
  const [currencySymbol, setCurrencySymbol] = useState(DEFAULT_CURRENCY_SYMBOL);
  // True once the persisted selection has actually been restored from
  // localStorage for the logged-in user. Consumers that decide whether the
  // current selection is "valid" (e.g. UserBox resetting to a default when
  // the account list loads) must wait for this — otherwise they can run
  // before storageKey is ready (user not loaded yet), see selectedAccount
  // still at its initial "", and wrongly stomp a real persisted account
  // with a default (confirmed live: reset to "All Accounts" on fresh loads).
  const [accountHydrated, setAccountHydrated] = useState(false);

  // Re-read from localStorage whenever the logged-in user changes
  useEffect(() => {
    if (!storageKey) return;
    const stored = localStorage.getItem(storageKey) || "";
    setSelectedAccount(stored);
    setAccountHydrated(true);
  }, [storageKey]);

  // Persist whenever selection changes
  useEffect(() => {
    if (storageKey) {
      localStorage.setItem(storageKey, selectedAccount);
    }
  }, [selectedAccount, storageKey]);

  // Fetch the selected account's currency symbol. Skipped for "All Accounts"
  // (no single per-account setting applies) and falls back to "$" on error.
  useEffect(() => {
    let cancelled = false;
    if (!selectedAccount || selectedAccount === "All Accounts") {
      setCurrencySymbol(DEFAULT_CURRENCY_SYMBOL);
      return;
    }
    axios
      .get("/settings/system", { params: { username: selectedAccount } })
      .then((res) => {
        if (!cancelled) {
          setCurrencySymbol(res.data?.currency_symbol || DEFAULT_CURRENCY_SYMBOL);
        }
      })
      .catch(() => {
        if (!cancelled) setCurrencySymbol(DEFAULT_CURRENCY_SYMBOL);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedAccount]);

  return (
    <AccountContext.Provider
      value={{
        selectedAccount,
        setSelectedAccount,
        demoAccounts,
        setDemoAccounts,
        currencySymbol,
        accountHydrated,
      }}
    >
      {children}
    </AccountContext.Provider>
  );
};

export const useAccount = () => useContext(AccountContext);
